import { useMutation, useQuery } from '@apollo/client';

import {
  APPLY_FORMAT_TEMPLATE,
  CREATE_FORMAT_TEMPLATE,
  DELETE_FORMAT_TEMPLATE,
  FORMAT_TEMPLATE_FIELDS,
  FORMAT_TEMPLATE_PREVIEW,
  GET_FORMAT_TEMPLATES,
  GET_FORMAT_TEMPLATE_STAGE_LIBRARY,
  ISSUE_FORMAT_TEMPLATE_DIAGRAM_UPLOAD,
  SET_FORMAT_TEMPLATE_GLOBAL,
  UPDATE_FORMAT_TEMPLATE,
  UPDATE_FORMAT_TEMPLATE_CATALOG,
} from '../queries/format-template.queries';
import { IBandDiagramInput, IStageSettingInput } from '../types/format-template.types';
import { createCacheModifier, removeCacheModifier } from '../cache/basic.cache';

// fetchWhenNoCompany lets an admin (who may have no active company) load global
// SYSTEM templates; otherwise the query skips until a companyId is available.
export const getFormatTemplates = (companyId?: string, options?: { fetchWhenNoCompany?: boolean }) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_FORMAT_TEMPLATES, {
    skip: !companyId && !options?.fetchWhenNoCompany,
    variables: { companyId },
    fetchPolicy: 'cache-and-network',
  });

  return {
    templates: (data?.formatTemplates ?? []) as IFormatTemplate[],
    loading,
    error,
  };
};

// Stable empty default so consumers' memos keyed on the library don't re-run every render.
const EMPTY_STAGE_LIBRARY: IFormatTemplateStageDefinition[] = [];

// Catalog-editor read: one template's stageLibrary, fetched only while the editor is open
// (skip otherwise) — the shared list fragment deliberately omits the heavy configs.
export const getFormatTemplateStageLibrary = (
  templateId?: string,
  companyId?: string,
  options?: { skip?: boolean },
) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(GET_FORMAT_TEMPLATE_STAGE_LIBRARY, {
    skip: options?.skip || !templateId,
    variables: { _id: templateId, companyId },
    fetchPolicy: 'cache-and-network',
  });

  const stageLibrary = (data?.formatTemplate?.stageLibrary ?? EMPTY_STAGE_LIBRARY) as IFormatTemplateStageDefinition[];
  return { stageLibrary, loading, error };
};

// Resolves the stages an apply would insert. Omit bandKey to let the server pick the band from
// the age group's live roster; pass one to override it. Skips until both ids are known.
export const getFormatTemplatePreview = ({ templateId, ageGroupId, bandKey }: {
  templateId?: string | null;
  ageGroupId?: string | null;
  bandKey?: string | null;
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { data, loading, error } = useQuery(FORMAT_TEMPLATE_PREVIEW, {
    skip: !templateId || !ageGroupId,
    variables: { templateId, ageGroupId, bandKey },
    fetchPolicy: 'cache-and-network',
  });

  return {
    preview: (data?.formatTemplatePreview ?? null) as IFormatTemplatePreview | null,
    loading,
    error,
  };
};

export const buildCreateFormatTemplate = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(CREATE_FORMAT_TEMPLATE);

  return {
    ...rest,
    createFormatTemplate: (data: {
      name: string;
      companyId: string;
      ageGroupBuckets?: string[];
      minPlayers?: number;
      maxPlayers?: number;
      stageBlueprints: IStageBlueprint[];
      teamOutcomes?: string[];
      imageUrl?: string;
    }) =>
      mutation({
        variables: { data },
        update: (cache, { data: result }) => {
          if (result?.createFormatTemplate) {
            cache.modify({
              fields: {
                formatTemplates: createCacheModifier({
                  cache,
                  createdDoc: result.createFormatTemplate,
                  fragmentName: 'FormatTemplateFields',
                  fragment: FORMAT_TEMPLATE_FIELDS,
                  modelName: 'FormatTemplate',
                  append: true,
                }),
              },
            });
          }
        },
      }),
  };
};

export interface IUpdateFormatTemplateData {
  name?: string;
  ageGroupBuckets?: string[];
  minPlayers?: number;
  maxPlayers?: number;
  teamOutcomes?: string[];
  stageBlueprints?: IStageBlueprint[];
  // CUSTOM only — points the document at an uploaded diagram; a CATALOG template's
  // diagrams are per band and go through buildUpdateCatalogFormatTemplate instead.
  imagePath?: string;
}

export const buildUpdateFormatTemplate = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_FORMAT_TEMPLATE);

  return {
    ...rest,
    // Apollo normalizes the returned _id + changed fields into the cached template —
    // the list re-renders without a refetch (nothing derived off these fields elsewhere).
    updateFormatTemplate: (_id: string, data: IUpdateFormatTemplateData) =>
      mutation({
        variables: { _id, data },
        // imageUrl is a server-signed read URL derived from imagePath — normalization can't
        // produce it, so a diagram swap re-reads the list.
        ...(data.imagePath !== undefined && { refetchQueries: ['GetFormatTemplates'] }),
        // Skip the optimistic write on a metadata-only update: the mutation selects
        // stageBlueprints, so omitting them would flash-clear the cached list.
        ...(data.stageBlueprints !== undefined && {
          optimisticResponse: {
            __typename: 'Mutation',
            updateFormatTemplate: {
              __typename: 'FormatTemplate',
              _id,
              name: data.name,
              ageGroupBuckets: data.ageGroupBuckets ?? [],
              minPlayers: data.minPlayers ?? null,
              maxPlayers: data.maxPlayers ?? null,
              teamOutcomes: data.teamOutcomes ?? [],
              stageBlueprints: data.stageBlueprints,
            },
          },
        }),
      }),
  };
};

// Catalog-only companion to buildUpdateFormatTemplate: stage settings and band diagrams are the
// two things an admin may change on a composed family — structure stays seed-owned.
export const buildUpdateCatalogFormatTemplate = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(UPDATE_FORMAT_TEMPLATE_CATALOG);

  return {
    ...rest,
    updateCatalogFormatTemplate: (_id: string, data: {
      stageSettings?: IStageSettingInput[];
      bandDiagrams?: IBandDiagramInput[];
    }) =>
      mutation({
        variables: { _id, data },
        // Band imageUrls are server-signed read URLs derived from the stored paths —
        // normalization can't produce them, so a diagram change re-reads the list.
        ...(data.bandDiagrams?.length && { refetchQueries: ['GetFormatTemplates'] }),
      }),
  };
};

export const buildIssueFormatTemplateDiagramUpload = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(ISSUE_FORMAT_TEMPLATE_DIAGRAM_UPLOAD);

  return {
    ...rest,
    // bandKey names a band on a CATALOG template and must be omitted for a CUSTOM one.
    issueFormatTemplateDiagramUpload: async (_id: string, contentType: string, bandKey?: string) => {
      const result = await mutation({ variables: { _id, contentType, bandKey } });
      return result.data?.issueFormatTemplateDiagramUpload as {
        uploadUrl: string;
        imagePath: string;
        contentType: string;
      } | undefined;
    },
  };
};

export const buildDeleteFormatTemplate = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(DELETE_FORMAT_TEMPLATE);

  return {
    ...rest,
    deleteFormatTemplate: (_id: string) =>
      mutation({
        variables: { _id },
        update: cache => {
          cache.modify({
            fields: {
              formatTemplates: removeCacheModifier(cache, _id),
            },
          });
        },
        optimisticResponse: {
          __typename: 'Mutation',
          deleteFormatTemplate: { __typename: 'FormatTemplate', _id },
        },
      }),
  };
};

export const buildSetFormatTemplateGlobal = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(SET_FORMAT_TEMPLATE_GLOBAL);

  return {
    ...rest,
    setFormatTemplateGlobal: (_id: string, value: boolean) =>
      mutation({
        variables: { _id, value },
        // The flip moves the template between company template lists, which normalization can't
        // express — without the re-read a demoted template lingers in the wrong company's list.
        refetchQueries: ['GetFormatTemplates'],
        // Promotion clears companyId, so the outcome is knowable from the request. Demotion's new
        // owner is server-assigned, so it waits for the response instead of guessing.
        ...(value && {
          optimisticResponse: {
            __typename: 'Mutation',
            setFormatTemplateGlobal: {
              __typename: 'FormatTemplate',
              _id,
              scope: 'SYSTEM',
              companyId: null,
            },
          },
        }),
      }),
  };
};

export const buildApplyFormatTemplate = () => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [mutation, rest] = useMutation(APPLY_FORMAT_TEMPLATE);

  return {
    ...rest,
    applyFormatTemplate: ({ assessmentId, templateId, ageGroupId, mode, bandKey }: {
      assessmentId: string;
      templateId: string;
      ageGroupId: string;
      mode: 'APPEND' | 'REPLACE';
      bandKey?: string | null;
    }) =>
      mutation({
        variables: { _id: assessmentId, templateId, ageGroupId, mode, bandKey },
        refetchQueries: ['GetAssessmentStageBuilder'],
        awaitRefetchQueries: true,
      }),
  };
};
